'use client';

import { useRightDrawer } from '@/contexts/DrawerContext';
import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';
import { researchFetch } from '@/lib/utils/fetch';
import { Loader } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Message } from '@/type/chat';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useTheme } from 'next-themes';

const Editor = dynamic(() => import('@toast-ui/react-editor').then((mod) => mod.Editor), {
  ssr: false,
});

const Viewer = dynamic(() => import('@toast-ui/react-editor').then((mod) => mod.Viewer), {
  ssr: false,
});

export default function MarkdownEditor({
  message,
  editReport,
  mdContent,
  version,
  editable = true,
}: {
  message: Message;
  editReport: (message: string) => void;
  mdContent?: string;
  version?: number;
  editable?: boolean;
}) {
  const { chatId, messageId, searchId } = message;
  const mkd = mdContent || message.content;
  const [height, setHeight] = useState(600);
  const [newMd, setNewMd] = useState<string>();
  const [updating, setUpdating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const { closeDrawer } = useRightDrawer();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showSuccess, showError } = useSnackbar();

  const hasChange = mkd !== message.content || (newMd && newMd !== message.content);

  const handleUpdate = async () => {
    if (updating) {
      return;
    }
    try {
      setUpdating(true);
      const editor = editorRef.current?.getInstance();
      const md = editable ? editor?.getMarkdown() : mkd;
      // if (!searchId) {
      let id = messageId;
      try {
        const r = await researchFetch(`api/search/${messageId}/report-id`).then((r) => r?.json());
        id = await r?.data?.messageId;
      } catch (error) {}
      const resp = await researchFetch(`api/conversations/${chatId}/messages/${id || messageId}`, {
        method: 'PUT',
        body: JSON.stringify({ content: md }),
      });
      const res = await resp?.json();
      if (res.success) {
        editReport(md);
        showSuccess('report save successfuly!');
        closeDrawer();
        editor?.destroy();
      } else {
        showError((res as any).error || 'report save failed!');
      }
    } catch (error) {
      showSuccess((error as any).message || 'report save failed!');
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = () => {
    const editor = editorRef.current?.getInstance();
    if (mkd !== editor?.getMarkdown()) {
      setNewMd(editor?.getMarkdown());
    }
  };

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, []);

  useEffect(() => {
    if (version && version >= 0) {
      editorRef.current?.getInstance().setMarkdown(mkd, false);
    }
  }, [mkd, version]);

  return (
    <div ref={ref} className="h-[calc(100vh-50px)] -m-4">
      <div className="flex flex-col gap-4 h-full *:*:first:border-none!">
        {editable ? (
          <Editor
            height={`${height - (hasChange ? 90 : 0)}px`}
            initialEditType="wysiwyg"
            previewStyle="vertical"
            initialValue={mkd}
            theme={theme}
            onChange={handleChange}
            ref={editorRef}
          />
        ) : (
          <div className={cn('px-4 overflow-auto h-[calc(100%-90px)]')}>
            <Viewer initialValue={mkd} theme={theme} ref={editorRef} />
          </div>
        )}

        {!editable && (
          <div className="border-t border-[#2a2a2a] p-4 mt-auto">
            <div className="flex items-center gap-3">
              <button
                className={cn(
                  'flex-1 h-[40px] rounded-[10px] bg-[#c91ae2] text-white font-medium text-sm flex items-center justify-center transition-colors',
                  { 'hover:bg-[#b015d0]': !!hasChange, 'cursor-not-allowed': !hasChange },
                )}
                onClick={handleUpdate}
              >
                {updating && <Loader className="animate-spin" size={16} />}
                {t('Restore this version')}
              </button>
              <button
                className="
              flex-1 h-[40px] rounded-[10px]
              bg-[#2a2a2a] text-white
              font-medium text-sm
              flex items-center justify-center
              hover:bg-[#3a3a3a] transition-colors
            "
                onClick={() => closeDrawer()}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        )}

        {hasChange && editable && (
          <div className="border-t border-[#2a2a2a] p-4 mt-auto">
            <div className="flex items-center gap-3">
              <button
                disabled={!hasChange || updating}
                className={cn(
                  'flex-1 h-[40px] rounded-[10px] bg-[#c91ae2] text-white font-medium text-sm flex items-center justify-center transition-colors',
                  { 'hover:bg-[#b015d0]': !!hasChange, 'cursor-not-allowed': !hasChange },
                )}
                onClick={handleUpdate}
              >
                {updating && <Loader className="animate-spin" size={16} />}
                {t('Update')}
              </button>
              <button
                className="
              flex-1 h-[40px] rounded-[10px]
              bg-[#2a2a2a] text-white
              font-medium text-sm
              flex items-center justify-center
              hover:bg-[#3a3a3a] transition-colors
            "
                onClick={() => closeDrawer()}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
